import { useContext, useState, useEffect } from "react";
import DownloadIcon from "@mui/icons-material/CreateNewFolder"; // Updated imports
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles"; // Using styled utility
import { useService } from "../applications-service";
import { ApplicationsContext } from "../applications-context";
import { StyledButton } from "../../../components/styled-button/styled-button";
import DateRangePicker from "../../../components/date-range-picker/date-range-picker";
import { AppConfig } from "../../../context/context";
import { saveAs } from 'file-saver'; // To handle file download
import { wrap } from "module";

const ChooseDatesContainer = styled('div')(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexWrap: "wrap", 
  justifyContent: "flex-start", 
  "& button.MuiButton-root": {
    "&.Mui-disabled": {
      opacity: "0.5 !important",
      backgroundColor: "var(--kyocera-bg-grey)",
      boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
      color: "var(--kyocera-text-grey)",
    },
    marginLeft: theme.spacing(2),
  },
}));

interface IProps {
  startDate?: Date | null;
  endDate?: Date | null;
  onDateChange?: (dates: [Date | null, Date | null]) => void;
}

const ChooseDatesReportComponent = ({ startDate: propsStart, endDate: propsEnd, onDateChange: propsOnChange }: IProps) => {
  const { appService } = useService();
  const { isAppListLoading, selectedApp, selectedCheckedApps, applications } = useContext(ApplicationsContext);
  const { t } = useTranslation();
  const [internalStartDate, setInternalStartDate] = useState<Date | null>(null);
  const [internalEndDate, setInternalEndDate] = useState<Date | null>(null);

  const startDate = propsStart !== undefined ? propsStart : internalStartDate;
  const endDate = propsEnd !== undefined ? propsEnd : internalEndDate;

  const [loading, setLoading] = useState(false); 
  const [selectedAppNames, setSelectedAppNames] = useState<string[]>([]);
  const { config } = useContext(AppConfig);

  const today = new Date();

  const minSelectableDate = new Date();
  minSelectableDate.setDate(today.getDate() - 30);
  minSelectableDate.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (selectedCheckedApps.length > 0 && applications?.devTenantApps) {
      const names = selectedCheckedApps.map(appId => {
      const app = applications.devTenantApps.find(app => app.id === appId);
        return app ? app.name : "";
      });
      setSelectedAppNames(names);
    } else {
      setSelectedAppNames([]);
    }
  }, [selectedCheckedApps, applications]);

  const getFormattedRange = (startDate: Date, endDate: Date) => {
    const formatWithOffset = (date: Date, isEnd: boolean) => {
      const d = new Date(date);
      
      // 1. Set boundaries to local start/end of day
      if (isEnd) {
        d.setHours(23, 59, 59, 999);
      } else {
        d.setHours(0, 0, 0, 0);
      }

      // 2. Build the string: YYYY-MM-DDTHH:mm:ss
      const pad = (n: number) => n.toString().padStart(2, '0');
      const t = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

      // 3. Calculate the Timezone Offset (e.g., +09:00 or -07:00)
      const offsetMinutes = d.getTimezoneOffset();
      const absOffset = Math.abs(offsetMinutes);
      const offsetHours = pad(Math.floor(absOffset / 60));
      const offsetMins = pad(absOffset % 60);
      const sign = offsetMinutes > 0 ? "-" : "+"; // getTimezoneOffset is inverted

      return `${t}${sign}${offsetHours}:${offsetMins}`;
    };

    return {
      startParam: formatWithOffset(startDate, false), // e.g., "2025-12-01T00:00:00+09:00"
      endParam: formatWithOffset(endDate, true)      // e.g., "2025-12-16T23:59:59+09:00"
    };
  };

  const formatDateForFilename = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (propsOnChange) {
      propsOnChange(dates);
    } else {
      setInternalStartDate(start);
      setInternalEndDate(end);
    }
  };
  
  const handleGenerateReport = async () => {
    if (!selectedApp || !startDate || !endDate) return;
    console.log('Generating report...');

    try {
      setLoading(true);

      const { startParam, endParam } = getFormattedRange(startDate, endDate);

      const response = await appService.getUsageReport(
        config.tenantId,
        selectedAppNames[0],
        encodeURIComponent(startParam),
        encodeURIComponent(endParam)
      );

      if (response) {
        const blob = new Blob([response], { type: 'text/csv' });

        const startFilename = formatDateForFilename(startDate);
        const endFilename = formatDateForFilename(endDate);
        const fileName = `Usage_${selectedAppNames[0]}_${startFilename}_${endFilename}.csv`;
        saveAs(blob, fileName); // Trigger download
      } else {
        console.error('Failed to generate report: No data received.');
      }
    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const isDownloadButtonDisabled =
    !startDate ||
    !endDate ||
    !selectedApp ||
    isAppListLoading ||
    loading;
    
  return (
    <ChooseDatesContainer>
        <DateRangePicker
          disabled={!selectedApp || isAppListLoading}
          placeHolderText={t("choose_dates")} 
          maxDate={new Date()} 
          minDate={minSelectableDate}
          startDate={startDate} 
          endDate={endDate} 
          onDateChange={handleDateChange}
        />
        <StyledButton
          variant="contained"
          onClick={handleGenerateReport}
          className="d-flex align-items-center"
          disabled={isDownloadButtonDisabled}
          startIcon={<DownloadIcon />}
        >
          {t("download_report")}
        </StyledButton>     
    </ChooseDatesContainer>
  );
};

export default ChooseDatesReportComponent;
